import { useCallback, useEffect, useRef, useState } from "react";
import { fetchOrgs, type OrgSummary } from "./api/orgs";
import { Authorize } from "./auth/Authorize";
import { AuthCallback } from "./auth/AuthCallback";
import { Login } from "./auth/Login";
import { clearSession, getSession, getToken, setOrgLogos } from "./auth/session";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AdminPage } from "./features/admin/AdminPage";
import { Chat } from "./features/chat/Chat";
import { DashboardsPage } from "./features/dashboards/DashboardsPage";
import { MetricsPage } from "./features/metrics/MetricsPage";
import { OnboardingPage } from "./features/onboarding/OnboardingPage";
import { ReportProvider } from "./features/report/ReportContext";
import { ReportDrawer } from "./features/report/ReportDrawer";
import { SharePage } from "./features/share/SharePage";
import { Sidebar } from "./features/threads/Sidebar";
import { TrainingPage } from "./features/training/TrainingPage";

// Local dev fallback so we can still drive the loop end-to-end without
// wiring Firebase locally. Production reads the real Agami access_token
// minted by the Google/Microsoft OAuth flow; this only kicks in when no
// token is in localStorage AND we're on localhost.
const DEV_BEARER = "agami-super-bypass";

function resolveBearer(): string {
  const real = getToken();
  if (real) return real;
  if (typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
    return DEV_BEARER;
  }
  return "";
}

export default function App() {
  // Trivial path-based routing — chat, dashboards, share viewer, and
  // the auth pages all live on the same SPA bundle. Anything richer
  // can swap to react-router; for now this keeps the bundle slim and
  // the auth flow obvious.
  const path = window.location.pathname;
  // OAuth redirect_uri registered with Google/Microsoft is
  // `{FRONTEND_BASE_URL}/google-callback` / `/callback`. The SPA owns
  // the callback so we don't have to round-trip through the backend.
  // PR-C retired the legacy /v2/* aliases; Netlify 301s anything that
  // still arrives at /v2/google-callback to /google-callback.
  if (path === "/google-callback") {
    return <AuthCallback provider="google" />;
  }
  if (path === "/callback") {
    return <AuthCallback provider="microsoft" />;
  }
  // MCP OAuth consent page — Claude.ai et al. land here via
  // /oauth/authorize, sign in, and bounce back.
  if (path === "/authorize") {
    return <Authorize />;
  }
  if (path === "/login" || path.startsWith("/login/")) {
    return <Login />;
  }
  // Public share viewer: no auth required.
  if (path.startsWith("/s/")) {
    return <SharePage />;
  }

  // Anything else requires a valid token. Localhost gets the dev
  // bypass so the inner-loop dev experience doesn't require OAuth.
  const bearer = resolveBearer();
  if (!bearer) {
    window.location.replace("/login");
    return null;
  }
  // Every authenticated view renders inside the same AppShell — the
  // shell owns the header + sidebar; the main content area swaps
  // based on path. Keeps the brand mark, nav, org switcher, and
  // avatar consistent across Search / Dashboards / Metrics.
  return <AppShell bearer={bearer} />;
}

interface AppShellProps {
  bearer: string;
}

function AppShell({ bearer }: AppShellProps) {
  // Section the URL is on. We drive the entire content area off this
  // so the header chrome (logo, nav, org switcher, avatar) is shared.
  // Subscribe to popstate so in-app navigation (history.pushState +
  // popstate dispatch from the dashboards list, for example) flips
  // the section without a full page reload.
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const onChange = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onChange);
    return () => window.removeEventListener("popstate", onChange);
  }, []);
  const currentSection:
    | "search"
    | "dashboards"
    | "metrics"
    | "admin"
    | "training"
    | "onboarding" = path.startsWith("/dashboards")
    ? "dashboards"
    : path.startsWith("/metrics")
      ? "metrics"
      : path.startsWith("/admin")
        ? "admin"
        : path.startsWith("/training")
          ? "training"
          : path.startsWith("/onboarding")
            ? "onboarding"
            : "search";
  // Bump-counter so the header re-reads localStorage after the
  // check_valid_user fetch lands new logo paths.
  const [logoRev, setLogoRev] = useState(0);
  useEffect(() => {
    // Stay quiet on the dev bypass path. Only fetch when the user has
    // a real Agami JWT in localStorage.
    if (bearer === DEV_BEARER) return;
    let cancelled = false;

    const check = async () => {
      try {
        const r = await fetch("/api/auth/check_valid_user", {
          headers: { Authorization: `Bearer ${bearer}` },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (cancelled) return;
        setOrgLogos(data.logo_light ?? null, data.logo_dark ?? null);
        setLogoRev((x) => x + 1);
      } catch {
        /* non-fatal — keep the default Agami mark */
      }
    };

    void check();
    // Re-check every 60s so mid-session org switches (which can change
    // the branded logo) get picked up without a manual reload.
    const id = window.setInterval(() => void check(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [bearer]);

  // Seed the active thread from `?thread=N` on first mount so an
  // admin-side "View Response" deep-link drops you into the exact
  // conversation that produced the queries. `?message=M` further
  // narrows the rendering to that single turn — used for the admin
  // per-row "View Response" jump.
  const initialThreadParams = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = Number(params.get("thread"));
      const m = Number(params.get("message"));
      return {
        thread: Number.isFinite(t) && t > 0 ? t : null,
        message: Number.isFinite(m) && m > 0 ? m : null,
      };
    } catch {
      return { thread: null as number | null, message: null as number | null };
    }
  })();
  const [activeThreadId, setActiveThreadId] = useState<number | null>(initialThreadParams.thread);
  const [focusMessageId] = useState<number | null>(initialThreadParams.message);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resetSignal, setResetSignal] = useState(0);
  // Sidebar starts collapsed — recent-threads list is hidden behind
  // the toggle to match the cleaner empty-state surface in v1.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  // Dry Run mode — when on, /chat requests carry `dry_run: true` so the
  // model emits SQL without executing it. Persisted to session storage
  // so a page reload doesn't lose the state mid-debug.
  const [dryRun, setDryRun] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem("agami_dry_run") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem("agami_dry_run", dryRun ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [dryRun]);

  // Org/datasource state lives here so the header can host the context
  // dropdown (matching v1's Super-Admin org switcher placement) while
  // Chat consumes the same values.
  const [orgList, setOrgList] = useState<OrgSummary[]>([]);
  const [orgListError, setOrgListError] = useState<string | null>(null);
  // Seed the org dropdown from the last value we persisted in
  // localStorage so the user's selection sticks across reloads /
  // tabs. Matches the legacy web behaviour.
  const [organization, setOrganization] = useState<string>(() => {
    try {
      return localStorage.getItem("agami_last_org") || "";
    } catch {
      return "";
    }
  });
  useEffect(() => {
    if (!organization) return;
    try {
      localStorage.setItem("agami_last_org", organization);
    } catch {
      /* ignore */
    }
  }, [organization]);
  // When a turn is running or a thread is loaded, lock the org
  // dropdown so the model's scope stays consistent.
  const [contextLocked, setContextLocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchOrgs(bearer)
      .then((orgs) => {
        if (cancelled) return;
        setOrgList(orgs);
        setOrganization((cur) => {
          // Keep the user's stored selection if it's still a valid org.
          if (cur && orgs.some((o) => o.org_id === cur)) return cur;
          const firstWithDs = orgs.find((o) => o.datasources.length > 0);
          return firstWithDs?.org_id ?? cur;
        });
      })
      .catch((e) => {
        if (!cancelled) setOrgListError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [bearer]);

  const handleThreadStarted = useCallback((threadId: number) => {
    setActiveThreadId(threadId);
    setRefreshKey((k) => k + 1);
  }, []);

  const handlePick = useCallback((id: number) => {
    setActiveThreadId(id);
  }, []);

  const handleNew = useCallback(() => {
    setActiveThreadId(null);
    setResetSignal((s) => s + 1);
  }, []);

  // When the loaded thread's org differs from the current selection,
  // Chat will call onContextSync to align the header. Datasource is
  // no longer pinned per turn, so we only sync the org.
  const handleContextSync = useCallback(
    (org: string) => {
      if (org && org !== organization) setOrganization(org);
    },
    [organization],
  );

  return (
    <ReportProvider>
    <div className="min-h-screen flex flex-col bg-white text-neutral-800">
      <header className="sticky top-0 z-30 border-b border-line bg-white">
        <div className="px-4 sm:px-6 h-[70px] grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="flex items-center min-w-0">
            <BrandMark organization={organization} key={`${organization}-${logoRev}`} />
          </div>
          <HeaderNav />
          <div className="flex items-center justify-end gap-3 min-w-0">
          <ContextDropdowns
            orgList={orgList}
            organization={organization}
            locked={contextLocked}
            onOrganizationChange={setOrganization}
          />
          {orgListError && (
            <span className="hidden sm:inline text-xs text-red-600 mr-2">{orgListError}</span>
          )}
          <UserMenu
            organization={organization}
            bearer={bearer}
            dryRun={dryRun}
            onDryRunChange={setDryRun}
          />
          </div>
        </div>
      </header>
      <div className="flex flex-1 min-h-0">
        {currentSection === "search" && (
          <Sidebar
            bearer={bearer}
            collapsed={sidebarCollapsed}
            activeThreadId={activeThreadId}
            refreshKey={refreshKey}
            onPick={handlePick}
            onNew={handleNew}
            onToggle={() => setSidebarCollapsed((c) => !c)}
          />
        )}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <ErrorBoundary>
            {currentSection === "admin" ? (
              <AdminPage bearer={bearer} />
            ) : currentSection === "training" ? (
              <TrainingPage bearer={bearer} />
            ) : currentSection === "onboarding" ? (
              <OnboardingPage bearer={bearer} />
            ) : currentSection === "dashboards" ? (
              <DashboardsPage bearer={bearer} organization={organization} />
            ) : currentSection === "metrics" ? (
              <MetricsPage
                bearer={bearer}
                organization={organization || getSession()?.defaultOrganization || ""}
              />
            ) : (
              <div className="max-w-3xl mx-auto px-6 py-10 min-h-[calc(100vh-70px)] flex flex-col">
                <Chat
                  bearer={bearer}
                  organization={organization}
                  dryRun={dryRun}
                  activeThreadId={activeThreadId}
                  focusMessageId={focusMessageId}
                  resetSignal={resetSignal}
                  onThreadStarted={handleThreadStarted}
                  onContextLockChange={setContextLocked}
                  onContextSync={handleContextSync}
                />
              </div>
            )}
          </ErrorBoundary>
        </main>
      </div>
      <ReportDrawer bearer={bearer} organization={organization} threadId={activeThreadId} />
    </div>
    </ReportProvider>
  );
}

/* ------------------------------------------------------------------ */
/* User menu — avatar in the header with a dropdown showing the        */
/* signed-in user + logout. M1 uses the dev bypass token; logout       */
/* clears it and reloads. M3 swaps this in for real Firebase claims.   */
/* ------------------------------------------------------------------ */

interface UserMenuProps {
  organization: string;
  bearer: string;
  dryRun: boolean;
  onDryRunChange: (v: boolean) => void;
}

function UserMenu({ organization: _organization, bearer, dryRun, onDryRunChange }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Reads the session persisted by the OAuth callback. On localhost
  // the dev bypass falls through and shows a stand-in identity so the
  // menu isn't blank during inner-loop dev.
  const session = getSession();
  const isDev = bearer === DEV_BEARER;
  const user = session
    ? {
        name: session.name || session.email.split("@")[0],
        email: session.email,
        role: session.role,
      }
    : {
        name: "Dev User",
        email: "local@agami.ai",
        role: "Super Admin",
      };
  const isSuperAdmin = user.role === "Super Admin";
  const isAdminOrAbove = isSuperAdmin || user.role === "Admin";
  const initials = user.name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const logout = () => {
    clearSession();
    // Land on the v2 login screen so the user can sign back in
    // without bouncing through the legacy app.
    window.location.assign("/login");
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          "h-9 w-9 rounded-full bg-brand text-white text-[13px] font-semibold uppercase tracking-wide flex items-center justify-center hover:bg-brand-600 transition-colors " +
          (open ? "ring-2 ring-brand/30 ring-offset-2" : "")
        }
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
      >
        {initials}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-[240px] rounded-[14px] bg-white border border-line shadow-card overflow-hidden z-50 text-[14px]"
        >
          {/* User identity. Bold name on top, muted role below — same
              two-line layout as v1's .user-dropdown. */}
          <div className="px-4 py-3">
            <div className="font-semibold text-neutral-900 truncate">{user.name}</div>
            <div className="text-[12px] text-neutral-500 truncate">{user.role}</div>
            {isDev && (
              <div className="text-[11px] text-amber-700 mt-0.5">dev bypass</div>
            )}
          </div>

          <Divider />

          {/* Dry Run toggle — emits SQL without executing it. The
              v2 backend reads `dry_run` off the /chat request body. */}
          <div className="px-4 py-2.5">
            <ToggleRow label="Dry Run" checked={dryRun} onChange={onDryRunChange} />
          </div>

          {/* Admin Console (web2 /admin) shows for Admin and Super
              Admin. Training Center still points at the legacy
              standalone page; it gets ported in a follow-up PR. */}
          {isAdminOrAbove && (
            <>
              <Divider />
              {isSuperAdmin && (
                <a
                  href="/training"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen(false);
                    if (window.location.pathname.startsWith("/training")) return;
                    window.history.pushState(null, "", "/training");
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-neutral-800 hover:bg-chip transition-colors"
                  role="menuitem"
                >
                  <SparklesIcon />
                  <span>Training Center</span>
                </a>
              )}
              <a
                href="/admin"
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  if (window.location.pathname.startsWith("/admin")) return;
                  window.history.pushState(null, "", "/admin");
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }}
                className="flex items-center gap-2.5 px-4 py-2.5 text-neutral-800 hover:bg-chip transition-colors"
                role="menuitem"
              >
                <EqualizerIcon />
                <span>Admin Console</span>
              </a>
            </>
          )}

          <Divider />
          <button
            type="button"
            onClick={logout}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-neutral-800 hover:bg-chip transition-colors"
            role="menuitem"
          >
            <LogoutIcon />
            <span>Log out</span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Logo block top-left. Always reflects the *currently selected* org
 * (the one in the context dropdown), not the user's default org.
 * The legacy api generates logo paths deterministically from the org
 * name, so we can do the same client-side without an extra fetch.
 * If the image 404s (org without a branded asset), the onError swap
 * falls back to the default Agami circle + wordmark.
 */
function BrandMark({ organization }: { organization: string }) {
  const lower = organization.toLowerCase();
  const ext = _ORGS_WITH_LOGOS.get(lower);
  if (ext) {
    return (
      <a href="/" className="inline-flex items-center hover:opacity-90 transition-opacity">
        <img
          src={`/images/${lower}/logo_light.${ext}`}
          alt={organization}
          className="h-10 w-auto max-w-[180px] object-contain"
        />
      </a>
    );
  }
  // No branded asset for this org: show the real Agami wordmark.
  return (
    <a href="/" className="inline-flex items-center hover:opacity-90 transition-opacity">
      <img src="/images/logo_h.svg" alt="Agami" className="h-9 w-auto object-contain" />
    </a>
  );
}

// Orgs that actually ship a branded logo under web2/public/images/<org>/.
// Mirrors the directory listing — keeps blank-tile flicker out of the
// swap when an org has no asset (instead of guessing + onError fallback).
const _ORGS_WITH_LOGOS: Map<string, "svg" | "png"> = new Map([
  ["sunmobility", "png"],
  ["benchmetrics", "svg"],
  ["meridiandemo", "svg"],
  ["servicenow", "svg"],
  ["stanford", "svg"],
  ["stateuniversity", "svg"],
]);

/**
 * Centred nav between the logo and the right-side controls. Mirrors
 * v1's Dashboard · Search tabs. Active tab gets a brand-coloured
 * underline (not a pill) to match v1's `.nav-underline` style.
 */
function HeaderNav() {
  const path = window.location.pathname;
  const items: { label: string; href: string; active: boolean }[] = [
    { label: "Dashboards", href: "/dashboards", active: path.startsWith("/dashboards") },
    { label: "Search", href: "/", active: path === "/" },
  ];
  return (
    <nav className="hidden sm:flex items-center gap-6 justify-self-center">
      {items.map((it) => (
        <a
          key={it.href}
          href={it.href}
          onClick={(e) => {
            // SPA-internal navigation — avoid a full page reload so
            // the org dropdown selection (and any other AppShell
            // state) persists across Search ↔ Dashboards.
            e.preventDefault();
            if (window.location.pathname === it.href) return;
            window.history.pushState(null, "", it.href);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }}
          className={
            "relative h-[70px] inline-flex items-center text-[15px] font-medium tracking-tight transition-colors " +
            (it.active
              ? "text-neutral-900 after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[2px] after:bg-brand"
              : "text-neutral-500 hover:text-neutral-900")
          }
        >
          {it.label}
        </a>
      ))}
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Compact org / datasource selector — replaces the inline ContextBar  */
/* that used to live inside Chat. Mirrors v1's header org switcher     */
/* placement for Super Admins.                                          */
/* ------------------------------------------------------------------ */

interface ContextDropdownsProps {
  orgList: OrgSummary[];
  organization: string;
  locked: boolean;
  onOrganizationChange: (v: string) => void;
}

/**
 * Header context — just the org switcher. The datasource picker used
 * to live here too but the v2 agent loop picks the right datasource
 * per question across every datasource in the org, so the picker was
 * just visual noise.
 */
function ContextDropdowns(props: ContextDropdownsProps) {
  return (
    <div className="flex items-center gap-1.5">
      <HeaderSelect
        value={props.organization}
        onChange={props.onOrganizationChange}
        options={props.orgList.map((o) => ({ value: o.org_id, label: o.name }))}
        disabled={props.locked || props.orgList.length === 0}
        ariaLabel="Organization"
      />
    </div>
  );
}

interface HeaderSelectProps {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled: boolean;
  ariaLabel: string;
}

function HeaderSelect({ value, onChange, options, disabled, ariaLabel }: HeaderSelectProps) {
  return (
    <div className="relative">
      <select
        aria-label={ariaLabel}
        className={
          "appearance-none pl-3 pr-8 h-9 rounded-full bg-transparent text-[15px] font-medium tracking-tight border border-transparent focus:outline-none focus:ring-1 focus:ring-brand " +
          (disabled
            ? "text-neutral-400 cursor-not-allowed"
            : "text-neutral-900 hover:bg-chip")
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.length === 0 && <option value="">—</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <svg
        viewBox="0 0 16 16"
        className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500 pointer-events-none"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6l4 4 4-4" />
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* User-menu helpers: divider, toggle row, icons.                       */
/* ------------------------------------------------------------------ */

function Divider() {
  return <div className="border-t border-line/70" />;
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer text-neutral-800">
      <span>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={
          "relative inline-flex h-5 w-9 items-center rounded-full transition-colors " +
          (checked ? "bg-brand" : "bg-neutral-300")
        }
      >
        <span
          className={
            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform " +
            (checked ? "translate-x-4" : "translate-x-0.5")
          }
        />
      </button>
    </label>
  );
}

function SparklesIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4 text-neutral-500">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l1.6 4.2L18 9l-4.4 1.8L12 15l-1.6-4.2L6 9l4.4-1.8L12 3zM18 14l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2zM6 16l.6 1.5 1.5.6-1.5.6L6 20l-.6-1.5-1.5-.6 1.5-.6L6 16z" />
    </svg>
  );
}

function EqualizerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4 text-neutral-500">
      <path strokeLinecap="round" d="M5 6h10M19 6h0M5 12h2M11 12h8M5 18h12M21 18h0" />
      <circle cx="17" cy="6" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="19" cy="18" r="2" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4 text-neutral-500">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3M10 17l-5-5 5-5M5 12h12" />
    </svg>
  );
}

